import { useState, useEffect, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';

// ─── Types ────────────────────────────────────────────────────────────────────
type N3 = [number, number, number];

interface PerioRecord {
  Sangrado: N3;
  Supuracion: N3;
  Placa: N3;
  AnchuraEncia: number;
  MargenGingival: N3;
  ProfundidadSondaje: N3;
}

type HalfData = Record<string, PerioRecord>;

interface PerioData {
  supVest: { izq: HalfData; der: HalfData };
  supPal:  { izq: HalfData; der: HalfData };
  infVest: { izq: HalfData; der: HalfData };
  infLing: { izq: HalfData; der: HalfData };
}

type ZoneKey = keyof PerioData;
type Arch = 'superior' | 'inferior';
type PerioSide = 'vest' | 'pal';
type Half = 'izq' | 'der';

// ─── Constants ────────────────────────────────────────────────────────────────
const SUP_IZQ = ['18','17','16','15','14','13','12','11'];
const SUP_DER = ['21','22','23','24','25','26','27','28'];
const INF_IZQ = ['48','47','46','45','44','43','42','41'];
const INF_DER = ['31','32','33','34','35','36','37','38'];

// SVG dimensions — viewBox matches rendered pixel size exactly (400×64)
// so 1 SVG unit = 1 px, aligned with TOOTH_W=50 HTML columns (8×50=400).
const SVG_W       = 400;
const SVG_H       = 64;
const TOOTH_SVG_W = SVG_W / 8; // 50 px per tooth
const TOOTH_STRIP = 12;        // px reserved at the anatomy edge for tooth labels
const MM_PX       = 6;         // pixels per millimetre
const MAX_MM      = (SVG_H - TOOTH_STRIP) / MM_PX; // ≈ 8.67 mm maximum shown

// Upper jaw: baseline at the TOP (y=TOOTH_STRIP), depth fills DOWNWARD — no empty space above.
// Lower jaw: baseline at the BOTTOM (y=SVG_H-TOOTH_STRIP), depth fills UPWARD — no empty space below.
const BASELINE_UP   = TOOTH_STRIP;          // 12
const BASELINE_DOWN = SVG_H - TOOTH_STRIP;  // 52

// Compute 3 probe-site x-positions (mesial, central, distal) for tooth column i
function toothXSites(i: number): [number, number, number] {
  const s = i * TOOTH_SVG_W;
  return [s + 10, s + 25, s + 40];
}

function defRec(): PerioRecord {
  return { Sangrado:[0,0,0], Supuracion:[0,0,0], Placa:[0,0,0], AnchuraEncia:0, MargenGingival:[0,0,0], ProfundidadSondaje:[0,0,0] };
}

function initHalf(teeth: string[]): HalfData {
  return Object.fromEntries(teeth.map(t => [t, defRec()]));
}

function initData(): PerioData {
  return {
    supVest: { izq: initHalf(SUP_IZQ), der: initHalf(SUP_DER) },
    supPal:  { izq: initHalf(SUP_IZQ), der: initHalf(SUP_DER) },
    infVest: { izq: initHalf(INF_IZQ), der: initHalf(INF_DER) },
    infLing: { izq: initHalf(INF_IZQ), der: initHalf(INF_DER) },
  };
}

function mergeHalf(def: HalfData, saved: HalfData | undefined): HalfData {
  if (!saved) return def;
  const result: HalfData = { ...def };
  for (const tooth of Object.keys(saved)) {
    result[tooth] = { ...(result[tooth] ?? defRec()), ...saved[tooth] };
  }
  return result;
}

function mergeData(def: PerioData, saved: Partial<PerioData>): PerioData {
  const result = { ...def };
  for (const zk of Object.keys(def) as ZoneKey[]) {
    const sz = (saved as Record<string, any>)[zk];
    if (sz) {
      result[zk] = {
        izq: mergeHalf(def[zk].izq, sz.izq),
        der: mergeHalf(def[zk].der, sz.der),
      };
    }
  }
  return result;
}

function getZone(arch: Arch, side: PerioSide): ZoneKey {
  if (arch === 'superior') return side === 'vest' ? 'supVest' : 'supPal';
  return side === 'vest' ? 'infVest' : 'infLing';
}

function getTeeth(arch: Arch, half: Half) {
  if (arch === 'superior') return half === 'izq' ? SUP_IZQ : SUP_DER;
  return half === 'izq' ? INF_IZQ : INF_DER;
}

// ─── SVG Graph ────────────────────────────────────────────────────────────────
function PerioGraph({ teeth, data, isUpper }: {
  teeth: string[];
  data: HalfData;
  isUpper: boolean;
}) {
  // Upper: baseline at TOP, depth fills DOWNWARD  (getY increases with val)
  // Lower: baseline at BOTTOM, depth fills UPWARD (getY decreases with val)
  const baseline = isUpper ? BASELINE_UP : BASELINE_DOWN;
  const getY = (val: number) =>
    isUpper
      ? BASELINE_UP + val * MM_PX
      : BASELINE_DOWN - val * MM_PX;

  const attachPts: string[] = [];
  const marginPts: string[] = [];
  teeth.forEach((tooth, i) => {
    const rec = data[tooth] ?? defRec();
    toothXSites(i).forEach((x, j) => {
      const gm = rec.MargenGingival[j] ?? 0;
      const ps = rec.ProfundidadSondaje[j] ?? 0;
      attachPts.push(`${x},${getY(gm + ps)}`);
      marginPts.push(`${x},${getY(gm)}`);
    });
  });

  const firstX = toothXSites(0)[0];
  const lastX  = toothXSites(7)[2];

  // Area 1 — Margen Gingival: from CEJ baseline to gum edge
  const mgFill = [`${firstX},${baseline}`, ...marginPts, `${lastX},${baseline}`].join(' ');

  // Area 2 — Prof. Sondaje: between gum edge and attachment level (the pocket)
  // Shape: trace forward along margin, then backward along attachment to close.
  const psFill = [...marginPts, ...[...attachPts].reverse()].join(' ');

  // Gridlines at every mm within the measurement zone
  const gridYs: number[] = [];
  for (let mm = 1; mm <= Math.floor(MAX_MM); mm++) {
    const y = getY(mm);
    if (y > TOOTH_STRIP - 1 && y < SVG_H - TOOTH_STRIP + 1) gridYs.push(Math.round(y));
  }

  // Tooth strip sits at top (upper) or bottom (lower)
  const stripY = isUpper ? 0 : SVG_H - TOOTH_STRIP;

  return (
    <svg
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      preserveAspectRatio="none"
      width="100%"
      height={SVG_H}
      className="block"
    >
      {/* Measurement zone background — gives the chart a grid appearance even when empty */}
      <rect x="0" y={isUpper ? TOOTH_STRIP : 0} width={SVG_W} height={SVG_H - TOOTH_STRIP} fill="#f0f4f8" />
      {/* Tooth-label strip */}
      <rect x="0" y={stripY} width={SVG_W} height={TOOTH_STRIP} fill="#e2e8f0" />

      {/* 1 mm gridlines */}
      {gridYs.map(y => (
        <line key={y} x1="0" y1={y} x2={SVG_W} y2={y} stroke="#cbd5e1" strokeWidth="0.5" />
      ))}
      {/* 3 mm bold gridlines */}
      {[3, 6].map(mm => {
        const y = Math.round(getY(mm));
        return (y > TOOTH_STRIP && y < SVG_H - TOOTH_STRIP)
          ? <line key={mm} x1="0" y1={y} x2={SVG_W} y2={y} stroke="#94a3b8" strokeWidth="1" />
          : null;
      })}

      {/* Vertical tooth column separators */}
      {Array.from({ length: 7 }, (_, i) => (i + 1) * TOOTH_SVG_W).map(x => (
        <line key={x} x1={x} y1="0" x2={x} y2={SVG_H} stroke="#cbd5e1" strokeWidth="0.5" />
      ))}

      {/* Baseline (gum level) */}
      <line x1="0" y1={baseline} x2={SVG_W} y2={baseline} stroke="#475569" strokeWidth="1.5" />

      {/* Area 1: Margen Gingival — baseline → gum edge (red tint) */}
      <polygon points={mgFill} fill="#fca5a535" />
      {/* Area 2: Prof. Sondaje — gum edge → attachment level (blue tint) */}
      <polygon points={psFill} fill="#3b82f650" />
      {/* Margen gingival line */}
      <polyline points={marginPts.join(' ')} fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinejoin="round" />
      {/* Attachment level line (MG + PS) */}
      <polyline points={attachPts.join(' ')} fill="none" stroke="#2563eb" strokeWidth="1.5" strokeLinejoin="round" />

      {/* Tooth number labels in the strip */}
      {teeth.map((tooth, i) => {
        const cx = i * TOOTH_SVG_W + TOOTH_SVG_W / 2;
        const ty = isUpper ? TOOTH_STRIP - 2 : SVG_H - 2;
        return (
          <text key={tooth} x={cx} y={ty} fontSize="9" textAnchor="middle" fill="#64748b" fontFamily="system-ui, sans-serif">
            {tooth}
          </text>
        );
      })}
    </svg>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Sq({ on, color, onClick }: { on: boolean; color: string; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      className={cn('w-5 h-5 border rounded transition-colors', onClick ? 'cursor-pointer' : '',
        on ? 'border-transparent' : 'border-gray-300 bg-white')}
      style={on ? { backgroundColor: color } : undefined}
    />
  );
}

function Toggle3({ vals, color, canEdit, onChange }: {
  vals: N3; color: string; canEdit: boolean; onChange?: (v: N3) => void;
}) {
  return (
    <div className="flex gap-1">
      {([0,1,2] as const).map(i => (
        <Sq key={i} on={vals[i] === 1} color={color} onClick={canEdit && onChange ? () => {
          const n = [...vals] as N3; n[i] = n[i] ? 0 : 1; onChange(n);
        } : undefined} />
      ))}
    </div>
  );
}

function Num3({ vals, canEdit, onChange }: { vals: N3; canEdit: boolean; onChange?: (v: N3) => void }) {
  return (
    <div className="flex gap-0.5">
      {([0,1,2] as const).map(i => (
        <input key={i} type="number" min="0" max="20"
          value={vals[i] || ''}
          disabled={!canEdit}
          onChange={e => {
            if (!onChange) return;
            const n = [...vals] as N3;
            n[i] = Math.max(0, Math.min(20, parseInt(e.target.value) || 0));
            onChange(n);
          }}
          className="w-8 h-7 text-center text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-primary-400 disabled:bg-gray-50 p-0"
        />
      ))}
    </div>
  );
}

// ─── Half panel ───────────────────────────────────────────────────────────────
// Label column is fixed; tooth columns use flex-1 so they fill 100% of available
// width.  The SVG uses width="100%" + preserveAspectRatio="none" so it scales
// with those same columns — no blank space on wide screens.
const LABEL_W  = 108;
const MIN_TOOTH = 44; // minimum px per tooth column for small screens

interface PerioHalfProps {
  halfLabel: string;
  teeth: string[];
  data: HalfData;
  isUpper: boolean;
  canEdit: boolean;
  onChange: (tooth: string, field: keyof PerioRecord, val: N3 | number) => void;
}

function PerioHalf({ halfLabel, teeth, data, isUpper, canEdit, onChange }: PerioHalfProps) {
  type RowDef = { key: string; label: string; render: (tooth: string) => ReactNode };

  const dataRows: RowDef[] = [
    { key: 'pieza', label: 'Pieza',
      render: t => <span className="text-[10px] font-bold text-text-primary">{t}</span> },
    { key: 'sang', label: 'Sangrado',
      render: t => <Toggle3 vals={data[t]?.Sangrado ?? [0,0,0]} color="#ef4444" canEdit={canEdit}
        onChange={v => onChange(t, 'Sangrado', v)} /> },
    { key: 'sup', label: 'Supuración',
      render: t => <Toggle3 vals={data[t]?.Supuracion ?? [0,0,0]} color="#f59e0b" canEdit={canEdit}
        onChange={v => onChange(t, 'Supuracion', v)} /> },
    { key: 'pla', label: 'Placa',
      render: t => <Toggle3 vals={data[t]?.Placa ?? [0,0,0]} color="#3b82f6" canEdit={canEdit}
        onChange={v => onChange(t, 'Placa', v)} /> },
    { key: 'anch', label: 'Anchura encía',
      render: t => (
        <input type="number" min="0" max="20"
          value={data[t]?.AnchuraEncia || ''}
          disabled={!canEdit}
          onChange={e => onChange(t, 'AnchuraEncia', parseInt(e.target.value) || 0)}
          className="w-12 h-7 text-center text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-primary-400 disabled:bg-gray-50 p-0"
        />
      ) },
    { key: 'mg', label: 'Margen gingival',
      render: t => <Num3 vals={data[t]?.MargenGingival ?? [0,0,0]} canEdit={canEdit}
        onChange={v => onChange(t, 'MargenGingival', v)} /> },
    { key: 'ps', label: 'Prof. sondaje',
      render: t => <Num3 vals={data[t]?.ProfundidadSondaje ?? [0,0,0]} canEdit={canEdit}
        onChange={v => onChange(t, 'ProfundidadSondaje', v)} /> },
  ];

  const rows = isUpper ? dataRows : [...dataRows].reverse();
  const minW = LABEL_W + 8 * MIN_TOOTH;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="text-[10px] font-bold text-text-secondary uppercase tracking-widest text-center py-1.5 bg-gray-50 border-b border-gray-200">
        {halfLabel}
      </div>
      {/* overflow-x-auto only kicks in on very narrow screens */}
      <div className="overflow-x-auto">
        <div style={{ minWidth: minW }}>
          {!isUpper && (
            <div style={{ marginLeft: LABEL_W }}>
              <PerioGraph teeth={teeth} data={data} isUpper={false} />
            </div>
          )}
          {rows.map(row => (
            <div key={row.key} className="flex border-b border-gray-100 last:border-0">
              <div className="flex-shrink-0 flex items-center px-2 bg-gray-50/60 border-r border-gray-100" style={{ width: LABEL_W }}>
                <span className="text-[10px] text-text-secondary leading-tight">{row.label}</span>
              </div>
              {teeth.map(tooth => (
                <div key={tooth} className="flex-1 flex items-center justify-center py-2">
                  {row.render(tooth)}
                </div>
              ))}
            </div>
          ))}
          {isUpper && (
            <div style={{ marginLeft: LABEL_W }}>
              <PerioGraph teeth={teeth} data={data} isUpper={true} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
interface Props { id: string; canEdit: boolean; tipo: 'adulto' | 'infantil'; consultaId?: string | null }

export function PeriodontogramaView({ id, canEdit, tipo, consultaId }: Props) {
  const qc = useQueryClient();
  const [arch, setArch] = useState<Arch>('superior');
  const [side, setSide] = useState<PerioSide>('vest');
  const [half, setHalf] = useState<Half>('izq');
  const [data, setData] = useState<PerioData>(initData());
  const [dirty, setDirty] = useState(false);

  const { data: saved, isLoading } = useQuery({
    queryKey: ['periodontograma', id, consultaId ?? null],
    queryFn: () => api.get(`/api/pacientes/${id}/periodontograma`, { params: { consultaId: consultaId ?? undefined } }).then(r => r.data),
  });

  useEffect(() => {
    if (saved?.datosJson) {
      setData(mergeData(initData(), saved.datosJson as Partial<PerioData>));
      setDirty(false);
    }
  }, [saved]);

  const saveMutation = useMutation({
    mutationFn: () => api.put(`/api/pacientes/${id}/periodontograma`, { datosJson: data, consultaId: consultaId ?? undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['periodontograma', id, consultaId ?? null] });
      toast.success('Periodontograma guardado');
      setDirty(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar')),
  });

  function updateRecord(zone: ZoneKey, h: Half, tooth: string, field: keyof PerioRecord, val: N3 | number) {
    setData(prev => ({
      ...prev,
      [zone]: {
        ...prev[zone],
        [h]: {
          ...prev[zone][h],
          [tooth]: { ...(prev[zone][h][tooth] ?? defRec()), [field]: val },
        },
      },
    }));
    setDirty(true);
  }

  if (tipo === 'infantil') {
    return (
      <div className="text-center py-10 border border-dashed border-gray-200 rounded-2xl text-sm text-text-secondary">
        El periodontograma aplica únicamente para dentición permanente (adulto).
      </div>
    );
  }

  if (isLoading) return <p className="text-center py-8 text-sm text-text-secondary">Cargando...</p>;

  const zone = getZone(arch, side);
  const isUpper = arch === 'superior';
  const teeth = getTeeth(arch, half);
  const halfData = data[zone][half];
  const halfLabel = half === 'izq'
    ? (arch === 'superior' ? 'Izquierdo — 18 · 17 · 16 · 15 · 14 · 13 · 12 · 11' : 'Izquierdo — 48 · 47 · 46 · 45 · 44 · 43 · 42 · 41')
    : (arch === 'superior' ? 'Derecho — 21 · 22 · 23 · 24 · 25 · 26 · 27 · 28' : 'Derecho — 31 · 32 · 33 · 34 · 35 · 36 · 37 · 38');

  return (
    <div className="space-y-3">
      {/* Control bar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
            {(['superior','inferior'] as Arch[]).map(a => (
              <button key={a} onClick={() => setArch(a)}
                className={cn('px-3 py-1.5 transition-colors capitalize', arch === a ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
                {a.charAt(0).toUpperCase() + a.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
            <button onClick={() => setSide('vest')}
              className={cn('px-3 py-1.5 transition-colors', side === 'vest' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
              Vestibular
            </button>
            <button onClick={() => setSide('pal')}
              className={cn('px-3 py-1.5 transition-colors', side === 'pal' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
              {arch === 'superior' ? 'Palatino' : 'Lingual'}
            </button>
          </div>
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
            <button onClick={() => setHalf('izq')}
              className={cn('px-3 py-1.5 transition-colors', half === 'izq' ? 'bg-violet-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
              Izquierdo
            </button>
            <button onClick={() => setHalf('der')}
              className={cn('px-3 py-1.5 transition-colors', half === 'der' ? 'bg-violet-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
              Derecho
            </button>
          </div>
        </div>
        {canEdit && dirty && (
          <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors">
            <Save size={12} /> {saveMutation.isPending ? 'Guardando...' : 'Guardar'}
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] text-text-secondary px-1 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-3 rounded" style={{ backgroundColor: '#fca5a535', border: '1.5px solid #ef4444' }} />
          <span>Margen gingival</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-3 rounded" style={{ backgroundColor: '#3b82f650', border: '1.5px solid #2563eb' }} />
          <span>Prof. sondaje</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded border border-transparent" style={{ backgroundColor: '#ef4444' }} />
          <span>BOP</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded border border-transparent" style={{ backgroundColor: '#f59e0b' }} />
          <span>Supuración</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded border border-transparent" style={{ backgroundColor: '#3b82f6' }} />
          <span>Placa</span>
        </div>
      </div>

      {/* Current half panel */}
      <PerioHalf
        halfLabel={halfLabel}
        teeth={teeth}
        data={halfData}
        isUpper={isUpper}
        canEdit={canEdit}
        onChange={(tooth, field, val) => updateRecord(zone, half, tooth, field, val)}
      />

      {canEdit && dirty && (
        <div className="flex justify-end">
          <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors">
            <Save size={14} /> {saveMutation.isPending ? 'Guardando...' : 'Guardar periodontograma'}
          </button>
        </div>
      )}
    </div>
  );
}
